import React, { useEffect, useState } from 'react'
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Paper, TextField, ThemeProvider } from '@mui/material';
import { MaterialActionElement, MaterialShiftActions, MaterialValueEditor, MaterialValueSelector, QueryBuilderMaterial } from '@react-querybuilder/material';
import { defaultOperators, defaultRuleProcessorMongoDB } from 'react-querybuilder';
import { QueryBuilder as MUIQueryBuilder, formatQuery, transformQuery } from 'react-querybuilder';
import { createTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import 'react-querybuilder/dist/query-builder.css';
import { DateTime } from 'luxon';

const MuiOutlinedValueEditor = (props) => {
    const extraProps = {
        variant: 'outlined',
        size: "small",
        ...(props.fieldData?.inputType === 'datetime-local' && {
            inputProps: {
                step: 1, // This enables seconds in the datetime-local input
            }
        })
    };


    // Si el operador es between o notBetween, necesitamos dos campos
    if (props.operator === 'between' || props.operator === 'notBetween') {
        return (
            <div className="flex gap-2">
                <MaterialValueEditor {...props} extraProps={extraProps} value={props.value.length === 1 ? props.value[0] : null} onChange={v => {
                    props.onChange([v, props.value?.[0]])
                }} />
                <MaterialValueEditor {...props} extraProps={extraProps} value={props.value.length === 1 ? props.value[1] : null} onChange={v => props.onChange([props.value?.[1], v])} />
            </div>
        );
    }

    return <MaterialValueEditor {...props} extraProps={extraProps} />;
};

const MuiOutlinedValueSelector = (props) => {
    return <MaterialValueSelector {...props} variant="outlined" size="small" />
}


const MuiOutlinedActionElement = (props) => (
    <MaterialActionElement {...props} variant="outlined" size="small" className="!bg-green-500 !text-dark-900 h-full" />
)

const FieldSelector = (props) => {

    return <Autocomplete
        title={props.title}
        value={props.value}
        size="small"
        className="field-selector"
        id={`field-selector-${props.value}`}
        freeSolo
        options={props.options}
        PaperComponent={(props) => <Paper  {...props} id="XXXXXX" width={{ width: "500px !important" }} />}
        renderOption={(props, option, state, ownerState) => {
            return (
                <li {...props} >
                    <div className="flex flex-row items-center w-full gap-2">
                        <div className="flex-1">
                            <p className="block w-full text-sm">{option.name || option}</p>
                            <p className="text-xs text-gray-600">{option.placeholder}</p>
                        </div>
                        <span className="text-xs bg-gray-600 rounded-full p-1 m-0">{option.inputType}</span>
                    </div>
                </li>
            )
        }}
        getOptionLabel={option => option.name || option}
        getOptionKey={option => option.name}
        onInputChange={(event, newInputValue) => {
            if (event && (event.type === "click" || (event.type === "keydown" && event.key === "Enter"))) {
                props.handleOnChange(newInputValue)
            }
        }}
        renderInput={(params) => (
            <TextField
                {...params}

                //{...props}
                className={props.className}
                disabled={props.disabled}
                placeholder={props.title}

                label="Field"
                onChange={(e) => props.handleOnChange(e.target.value)}
                onFocus={(event) => event.target.select()}
            />
        )}
    />
}

// InputType = "button" | "checkbox" | "color" | "date" | "datetime-local" | "email" | "file" | "hidden" | "image" | "month" | "number" | "password" | "radio" | "range" | "reset" | "search" | "submit" | "tel" | "text" | "time" | "url" | "week" | (string & {});
// "=" | "!=" | "<" | ">" | "<=" | ">=" | "contains" | "beginsWith" | "endsWith" | "doesNotContain" | "doesNotBeginWith" | "doesNotEndWith" | "null" | "notNull" | "in" | "notIn" | "between" | "notBetween";

export const datetimeOperators = [
    ...defaultOperators.filter(op => ['=', '!=', '<', '>', '<=', '>='].includes(op.name))
]

export const keyOperators = [
    ...defaultOperators.filter(op => ['='].includes(op.name))
]

export const booleanOperators = [
    ...defaultOperators.filter(op => ['='].includes(op.name))
];

export const getOperators = (fieldName, fieldType) => {
    if(fieldName === "key") {
        return keyOperators;
    }
    switch (fieldType) {
        case "string":
            return defaultOperators.filter(op => 
                ['=', '!=', 'contains', 'beginsWith', 'endsWith', 'doesNotContain', 'doesNotBeginWith', 'doesNotEndWith', 'null', 'notNull'].includes(op.name)
            );
        case "datetime":
            return datetimeOperators
        case "date":
                return datetimeOperators
        case "number":
            return defaultOperators.filter(op => 
                ['=', '!=', '<', '>', '<=', '>=', 'null', 'notNull'].includes(op.name)
            );
        case "boolean":
            return booleanOperators;
        case "metadata":
            return [...defaultOperators.filter(op => ['='].includes(op.name))];
        default:
            return defaultOperators.filter(op => ['between', 'notBetween']);
    }
}

const customRuleProcessor = (field, options, meta, fields) => {
    let _field = field;
    const fieldData = fields.find(f => f.name === field.field);
    const fieldType = fieldData?.type;

    if (!fields.map(x => x.name).includes(field.field)) {
        let value = ""
        if (typeof field === "string" && field.startsWith("\"") && field.endsWith("\"")) {
            value = "/" + field.substring(1, field.length - 1) + "/i"
        } else {
            try {
                
                value = Number(field.value);
                if (isNaN(value)) {
                    value = field.value
                }
            } catch (error) {
                value = field.value
            }
        }

        _field = {
            ...field,
            value: value,
            field: `${field.field}`
        }
    }
    
    let value = defaultRuleProcessorMongoDB(_field, options);

    if (fieldType === "datetime-local" || fieldType === "datetime" || fieldType === "date") {
        const dateValue = new Date(field.value);
        if (!isNaN(dateValue.getTime())) {
            let dateValueString = `ISODate("${DateTime.fromJSDate(dateValue).toISO()}")`
            value = value.replaceAll(`"${_field.value}"`, dateValueString)
        }
    }

    return value;
};

const muiTheme = createTheme({});

const QueryBuilder = ({ fields, initialQuery, onApply, cleanFilters, fireInitialApply = true }) => {

    const [expand, setExpand] = useState(true)
    const [debug, setDebug] = useState(false)
    const [query, setQuery] = useState(initialQuery);

    useEffect(() => {
        if (fireInitialApply) {
            _onApply();
        }
    }, [])

    
    const _onApply = () => {
        if(!query) return;

        const q = formatQuery(query, {
            format: "mongodb",
            valueProcessor: defaultRuleProcessorMongoDB,
            ruleProcessor: (rule, options, meta) => customRuleProcessor(rule, options, meta, fields)
        })
        onApply(q, query);
    }



    return (
        <div className="grid">
            <div className="hidden col-span-1 md:col-span-2 lg:col-span-3" />
            <Accordion expanded={expand} onChange={() => setExpand(!expand)} className="rounded-none shadow-none">
                <AccordionSummary expandIcon={<ExpandMoreIcon className="text-gray-400" />} >
                    <p className="font-bold "><FilterAltIcon /> Filters </p>
                </AccordionSummary>
                <AccordionDetails className="rounded-none shadow-none">
                    <div className="grid gap-8">
                        <ThemeProvider theme={muiTheme}>
                            <QueryBuilderMaterial  >
                                <MUIQueryBuilder
                                    fields={fields}
                                    query={query}
                                    onQueryChange={setQuery}
                                    resetOnFieldChange={true}
                                    resetOnOperatorChange={false}
                                    parseNumbers={'strict-limited'}
                                    autoSelectField={true}
                                    autoSelectOperator={"="}
                                    controlClassnames={{ queryBuilder: 'justifiedLayout queryBuilder-branches' }}
                                    controlElements={{
                                        valueEditor: MuiOutlinedValueEditor,
                                        valueSelector: MuiOutlinedValueSelector,
                                        actionElement: MuiOutlinedActionElement,
                                        fieldSelector: FieldSelector
                                    }}
                                />
                            </QueryBuilderMaterial>
                        </ThemeProvider>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                    <FormControlLabel
                                        value={debug}
                                        control={<Checkbox checked={debug} name="debug" />}
                                        label="Show debug query"
                                        onChange={() => setDebug(!debug)}
                                    />
                                </FormGroup>
                            </FormControl>
                            <div className="grid grid-cols-2 gap-4 md:col-start-4">
                                <Button variant="outlined" onClick={cleanFilters}>CLEAN</Button>
                                <Button variant="contained" onClick={_onApply} >APPLY</Button>
                            </div>
                        </div>
                    </div>
                </AccordionDetails>
                <AccordionActions className="!m-0 !p-0">
                    {debug && (
                        <div className="bg-gray-200 w-full  p-4 ">
                            <pre className="max-w-full text-wrap text-xs">
                                {formatQuery(query, {
                                    format: "mongodb",
                                    //parseNumbers: parseNumber,
                                    //valueProcessor: defaultRuleProcessorMongoDB,
                                    ruleProcessor: (rule, options, meta) => customRuleProcessor(rule, options, meta, fields)
                                })}
                            </pre>
                        </div>
                    )}
                </AccordionActions>
            </Accordion >
        </div>
    )
}

export default QueryBuilder;